import { useState, useEffect, useMemo } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { db, LEGACY_TENANT_ID } from "@/lib/firebase";
import { cache } from "@/lib/cache";
import { writeBatch, doc, updateDoc, collection, deleteDoc, getDocs, query, where, runTransaction } from "firebase/firestore";
import { MARKET_LEADS_COLLECTION, LEAD_ASSIGNMENTS_COLLECTION } from "@/services/marketLeadsService";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search,
  Filter,
  ChevronLeft,
  Plus,
  Tag,
  DollarSign,
  Briefcase,
  ArrowUpDown,
  MoreHorizontal,
  Clock,
  Users,
  Upload,
  FileUp,
  ListPlus,
  X,
  FolderEdit,
  Check,
  Pencil,
  Move,
  Trash2,
  ArrowRight,
  Star,
  Mail,
  RefreshCw
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fetchLeadsForCurrentUser } from "@/services/leadsService";
import { Lead } from "@/components/lead-card";
import {
  fetchMarketLeads,
  createMarketListing,
  purchaseLead,
  MarketLead,
  LeadOrder,
  createLeadOrder,
  getPendingLeadOrders,
  updateLeadOrderStatus,
  addLeadsToMarketplace,
  createFolderPackage,
  purchaseLeadPackage,
  LeadPackage,
  addOldContactToMarketplace,
  assignLeadsToOrder
} from "@/services/marketLeadsService";
import { CSVImport } from "@/components/csv-import";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Folder } from "lucide-react";
import { assignLead, transferLead } from "@/services/leadsService";
import { useUsers } from "@/hooks/use-users";
import { useFolders } from "@/hooks/use-folders";
import { useHideDeactivatedUsers } from "@/hooks/use-hide-deactivated-users";
import { cn } from "@/lib/utils";
import { LEADS_COLLECTION } from "@/services/leadsService";
import { LEAD_ORDERS_COLLECTION } from "@/services/marketLeadsService";

type SortField = "name" | "company" | "price" | "folderName" | "listedDate" | "followUpDate" | "status" | "priority" | "nextContactDate" | "profession" | "email" | "phone";
type SortDirection = "asc" | "desc";

// Add new constant for lead packages
// const LEAD_PACKAGES = [
//   { size: 5, label: "5 Leads" },
//   { size: 10, label: "10 Leads" },
//   { size: 20, label: "20 Leads" },
//   { size: 30, label: "30 Leads" },
//   { size: 50, label: "50 Leads" },
//   { size: 100, label: "100 Leads" }
// ];

export default function LeadMarketPage() {
  const { toast } = useToast();
  const { currentUser } = useAuth();

  // Helper function to safely extract date value from various formats
  const getDateValue = (dateField: any): string | null => {
    if (!dateField) return null;
    if (typeof dateField === 'string' && dateField.trim() !== '') return dateField;
    if (typeof dateField === 'object' && dateField.__time__) return dateField.__time__;
    if (dateField instanceof Date) return dateField.toISOString();
    // Handle timestamp objects that might have different structures
    if (typeof dateField === 'object' && dateField.seconds) {
      return new Date(dateField.seconds * 1000).toISOString();
    }
    return null;
  };
  const [ownLeads, setOwnLeads] = useState<Lead[]>([]);
  const [orders, setOrders] = useState<LeadOrder[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [newListingLead, setNewListingLead] = useState<Lead | null>(null);
  const [newListingPrice, setNewListingPrice] = useState("");
  const [newListingFolder, setNewListingFolder] = useState("");
  const [newListingDescription, setNewListingDescription] = useState("");
  const [newOrderAmount, setNewOrderAmount] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [sortField, setSortField] = useState<SortField>("listedDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [activeTab, setActiveTab] = useState("new-leads");
  const [isAddLeadsDialogOpen, setIsAddLeadsDialogOpen] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<Lead[]>([]);
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkFolder, setBulkFolder] = useState("");
  const [bulkDescription, setBulkDescription] = useState("");
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importPrice, setImportPrice] = useState("");
  const [importFolder, setImportFolder] = useState("");
  const [importDescription, setImportDescription] = useState("");
  const [marketData, setMarketData] = useState<{
    newLeads: MarketLead[],
    oldContacts: MarketLead[],
    packages: LeadPackage[]
  }>({ newLeads: [], oldContacts: [], packages: [] });
  const [folders, setFolders] = useState<string[]>([]);
  const [newFolderName, setNewFolderName] = useState("");
  const [isFolderManagementOpen, setIsFolderManagementOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<{ oldName: string; newName: string } | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);
  const [movingLeads, setMovingLeads] = useState<{ folder: string; leads: MarketLead[] } | null>(null);
  const [movingLead, setMovingLead] = useState<MarketLead | null>(null);
  const { users, isLoading: isLoadingUsers, error: usersError } = useUsers(currentUser?.tenantId);
  const [hideDeactivated] = useHideDeactivatedUsers();
  const visibleUsers = useMemo(
    () => (hideDeactivated ? users.filter(u => u.active !== false) : users),
    [users, hideDeactivated]
  );
  const [assigningLead, setAssigningLead] = useState<MarketLead | null>(null);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");

  // Get folders for the selected user
  const { folders: userFolders } = useFolders(selectedUser);
  const [bulkAssigningFolder, setBulkAssigningFolder] = useState<{ folder: string; leads: MarketLead[] } | null>(null);
  const [bulkTransferringFolder, setBulkTransferringFolder] = useState<{ folder: string; leads: MarketLead[] } | null>(null);
  const [deletingLead, setDeletingLead] = useState<MarketLead | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<{ folder: string; leads: MarketLead[] } | null>(null);
  const [isCountAssignDialogOpen, setIsCountAssignDialogOpen] = useState(false);
  const [countAssigningFolder, setCountAssigningFolder] = useState<{ folder: string; leads: MarketLead[] } | null>(null);
  const [assignCount, setAssignCount] = useState<string>("");
  const [isAssigningCount, setIsAssigningCount] = useState(false);
  const [isAssigningLead, setIsAssigningLead] = useState(false);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [isBulkAssigningSelected, setIsBulkAssigningSelected] = useState(false);
  // Add new state for custom lead count
  const [customLeadCounts, setCustomLeadCounts] = useState<Record<string, string>>({});
  const [isAddOldContactDialogOpen, setIsAddOldContactDialogOpen] = useState(false);
  const [availableOldContacts, setAvailableOldContacts] = useState<Lead[]>([]);
  const [selectedOldContact, setSelectedOldContact] = useState<Lead | null>(null);
  const [newOldContactPrice, setNewOldContactPrice] = useState("");
  const [newOldContactDescription, setNewOldContactDescription] = useState("");
  const [newOldContactFolder, setNewOldContactFolder] = useState("Altkontakte");
  // Add state for follow-up date filtering
  const [followUpFilter, setFollowUpFilter] = useState<"all" | "future" | "overdue">("all");
  const [selectedLeadsForAssignment, setSelectedLeadsForAssignment] = useState<Set<string>>(new Set());
  // Add state for manual refresh button
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showOnlyLeadsWithFollowUp, setShowOnlyLeadsWithFollowUp] = useState(false);

  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "developer";

  // Load leads and orders
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        if (currentUser) {
          // Load user's own leads with role-based filtering
          const fetchedLeads = await fetchLeadsForCurrentUser(
            currentUser.id,
            currentUser.role,
            currentUser.tenantId
          );
          setOwnLeads(fetchedLeads);

          // Fetch marketplace leads
          const fetchedMarketData = await fetchMarketLeads(currentUser.tenantId);
          setMarketData(fetchedMarketData);

          // Extract unique folder names from market leads
          const uniqueFolders = new Set<string>();
          fetchedMarketData.newLeads.forEach(lead => {
            if (lead.folderName) {
              uniqueFolders.add(lead.folderName);
            }
          });
          setFolders(Array.from(uniqueFolders));

          // Load all orders if admin
          if (isAdmin) {
            const ordersRef = collection(db, LEAD_ORDERS_COLLECTION);
            let q;

            if (currentUser.tenantId === "legacy") {
              q = query(ordersRef);
            } else {
              q = query(ordersRef, where("tenantId", "==", currentUser.tenantId));
            }

            const querySnapshot = await getDocs(q);
            const allOrders = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...(doc.data() as any)
            } as LeadOrder)).filter(order => {
              // Additional client-side filtering for legacy tenant to exclude other tenants
              if (currentUser.tenantId === "legacy") {
                return !order.tenantId || order.tenantId === "legacy";
              }
              return true;
            });

            setOrders(allOrders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast({
          title: "Fehler beim Laden der Daten",
          description: "Die Daten konnten nicht geladen werden.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [currentUser, toast, isAdmin]);

  // Handle manual refresh of marketplace data
  const handleManualRefresh = async () => {
    if (!currentUser) return;
    setIsRefreshing(true);
    try {
      const fresh = await fetchMarketLeads(currentUser.tenantId);
      setMarketData(fresh);
      toast({
        title: "Daten aktualisiert",
        description: "Marktplatz-Daten wurden neu geladen"
      });
    } catch (error) {
      console.error("Error refreshing market data:", error);
      toast({
        title: "Fehler",
        description: "Daten konnten nicht aktualisiert werden",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Group leads by folder
  const groupedNewLeads = marketData.newLeads.reduce((acc, lead) => {
    const folder = lead.folderName;
    if (!acc[folder]) {
      acc[folder] = [];
    }
    acc[folder].push(lead);
    return acc;
  }, {} as Record<string, MarketLead[]>);

  // Filter leads
  const filteredLeads = [...marketData.newLeads, ...marketData.oldContacts].filter(lead => {

    const matchesType =
      (activeTab === "new-leads" && lead.type === "new") ||
      (activeTab === "old-contacts" && lead.type === "old-contact");

    // Next contact date filtering (only applies to old-contacts tab)
    const matchesFollowUp = activeTab !== "old-contacts" || (() => {
      // Step 1: Apply checkbox filter (if checked, exclude leads without nextContactDate)
      if (showOnlyLeadsWithFollowUp && !lead.leadData.nextContactDate) {
        return false;
      }

      // Step 2: Apply dropdown filter
      if (followUpFilter === "all") {
        return true; // Show all remaining leads (after checkbox filter)
      }

      // For future/overdue filters, we need a valid nextContactDate
      if (!lead.leadData.nextContactDate) {
        return false; // Can't be future or overdue without a date
      }

      // Step 3: Apply date-based filtering for future/overdue
      const nextContactDate = new Date(getDateValue(lead.leadData.nextContactDate) || '');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      nextContactDate.setHours(0, 0, 0, 0);

      if (followUpFilter === "future") return nextContactDate > today;
      if (followUpFilter === "overdue") return nextContactDate < today;

      return true;
    })();

    return matchesType && matchesFollowUp && lead.status === "available";
  });

  // Sort leads
  const sortedLeads = [...filteredLeads].sort((a, b) => {
    switch (sortField) {
      case "price":
        return sortDirection === "asc" ? a.price - b.price : b.price - a.price;
      case "listedDate":
        return sortDirection === "asc"
          ? new Date(a.listedDate).getTime() - new Date(b.listedDate).getTime()
          : new Date(b.listedDate).getTime() - new Date(a.listedDate).getTime();
      case "followUpDate":
        if (!a.followUpDate || !b.followUpDate) return 0;
        return sortDirection === "asc"
          ? new Date(a.followUpDate).getTime() - new Date(b.followUpDate).getTime()
          : new Date(b.followUpDate).getTime() - new Date(a.followUpDate).getTime();
      case "name": {
        const aName = `${a.leadData.firstName} ${a.leadData.lastName}`.toLowerCase();
        const bName = `${b.leadData.firstName} ${b.leadData.lastName}`.toLowerCase();
        return sortDirection === "asc"
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName);
      }
      case "company": {
        const aCompany = a.leadData.company.toLowerCase();
        const bCompany = b.leadData.company.toLowerCase();
        return sortDirection === "asc"
          ? aCompany.localeCompare(bCompany)
          : bCompany.localeCompare(aCompany);
      }
      case "folderName": {
        const aFolder = a.folderName?.toLowerCase();
        const bFolder = b.folderName?.toLowerCase();
        return sortDirection === "asc"
          ? aFolder?.localeCompare(bFolder)
          : bFolder?.localeCompare(aFolder);
      }
      case "status": {
        const aStatus = a.status?.toLowerCase() || "";
        const bStatus = b.status?.toLowerCase() || "";
        return sortDirection === "asc"
          ? aStatus.localeCompare(bStatus)
          : bStatus.localeCompare(aStatus);
      }
      case "priority": {
        const priorityOrder = { "hoch": 3, "mittel": 2, "niedrig": 1 };
        const aPriority = priorityOrder[a.leadData.priority as keyof typeof priorityOrder] || 0;
        const bPriority = priorityOrder[b.leadData.priority as keyof typeof priorityOrder] || 0;
        return sortDirection === "asc" ? aPriority - bPriority : bPriority - aPriority;
      }
      case "nextContactDate": {
        const aDate = a.leadData.nextContactDate ? new Date(a.leadData.nextContactDate).getTime() : 0;
        const bDate = b.leadData.nextContactDate ? new Date(b.leadData.nextContactDate).getTime() : 0;
        return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
      }
      case "profession": {
        const aProfession = a.leadData.profession?.toLowerCase() || "";
        const bProfession = b.leadData.profession?.toLowerCase() || "";
        return sortDirection === "asc"
          ? aProfession.localeCompare(bProfession)
          : bProfession.localeCompare(aProfession);
      }
      case "email": {
        const aEmail = a.leadData.email?.toLowerCase() || "";
        const bEmail = b.leadData.email?.toLowerCase() || "";
        return sortDirection === "asc"
          ? aEmail.localeCompare(bEmail)
          : bEmail.localeCompare(aEmail);
      }
      case "phone": {
        const aPhone = a.leadData.phone || "";
        const bPhone = b.leadData.phone || "";
        return sortDirection === "asc"
          ? aPhone.localeCompare(bPhone)
          : bPhone.localeCompare(aPhone);
      }
      default:
        return 0;
    }
  });

  // First get filtered old contacts from the sorted/filtered leads
  const filteredOldContacts = sortedLeads.filter(lead => lead.type === "old-contact");

  // Group filtered old contacts by folder instead of advisor
  const groupedOldContacts = filteredOldContacts.reduce((acc, lead) => {
    const folder = lead.folderName || "System"; // Use "System" as default folder
    if (!acc[folder]) {
      acc[folder] = [];
    }
    acc[folder].push(lead);
    return acc;
  }, {} as Record<string, MarketLead[]>);

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle lead selection for assignment
  const handleLeadSelection = (leadId: string, isSelected: boolean) => {
    const newSelection = new Set(selectedLeadsForAssignment);
    if (isSelected) {
      newSelection.add(leadId);
    } else {
      newSelection.delete(leadId);
    }
    setSelectedLeadsForAssignment(newSelection);
  };

  // Handle select all leads in current view
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allLeadIds = new Set(sortedLeads.map(lead => lead.id));
      setSelectedLeadsForAssignment(allLeadIds);
    } else {
      setSelectedLeadsForAssignment(new Set());
    }
  };

  // Handle bulk assignment of selected leads (transactional, race-safe)
  const handleBulkAssignSelectedLeads = async () => {
    if (!selectedUser || selectedLeadsForAssignment.size === 0) {
      toast({ title: "Auswahl erforderlich", description: "Bitte wählen Sie einen Berater und mindestens einen Lead aus.", variant: "destructive" });
      return;
    }
    if (!currentUser?.id) {
      toast({ title: "Fehler", description: "Kein aktueller Benutzer gefunden.", variant: "destructive" });
      return;
    }
    if (isBulkAssigningSelected) return;
    setIsBulkAssigningSelected(true);

    const selectedLeads = sortedLeads.filter(lead => selectedLeadsForAssignment.has(lead.id));
    const CHUNK = 100;
    const currentDate = new Date().toLocaleDateString('de-DE');
    const assignedIds: string[] = [];
    const skippedIds: string[] = [];

    try {
      for (let start = 0; start < selectedLeads.length; start += CHUNK) {
        const chunk = selectedLeads.slice(start, start + CHUNK);
        let chunkAssigned: string[] = [];
        let chunkSkipped: string[] = [];

        await runTransaction(db, async (tx) => {
          chunkAssigned = [];
          chunkSkipped = [];

          const reads = await Promise.all(
            chunk.map(lead => tx.get(doc(db, MARKET_LEADS_COLLECTION, lead.id)))
          );

          for (let i = 0; i < chunk.length; i++) {
            const lead = chunk[i];
            const snap = reads[i];
            if (!snap.exists()) { chunkSkipped.push(lead.id); continue; }
            const data = snap.data() as MarketLead;
            if (data.status !== "available") { chunkSkipped.push(lead.id); continue; }

            const marketplacePurchaseInfo = lead.type === "old-contact"
              ? `Über Marktplatz zugewiesen von System am ${currentDate}`
              : `Über Marktplatz zugewiesen von ${lead.sellerName} am ${currentDate}`;

            const cleanLeadData = Object.fromEntries(
              Object.entries(lead.leadData).filter(([_, value]) => value !== undefined)
            );

            const newLeadRef = doc(collection(db, LEADS_COLLECTION));
            const newLeadData = {
              ...cleanLeadData,
              id: newLeadRef.id,
              assignedTo: selectedUser,
              status: lead.type === "old-contact" ? "old-contact" : "new",
              createdAt: cleanLeadData.createdAt || new Date().toISOString(),
              lastUpdated: new Date().toISOString(),
              nextContactDate: lead.type === "old-contact" ? getDateValue(cleanLeadData.nextContactDate) : null,
              source: "market",
              marketLeadId: lead.id,
              marketplacePurchaseInfo,
              tenantId: data.tenantId || LEGACY_TENANT_ID,
              folderId: selectedFolderId || null,
              history: [
                ...(cleanLeadData.history || []),
                {
                  type: "marketplace_assignment",
                  date: new Date().toISOString(),
                  user: currentUser.id,
                  changes: [{ field: "assignedTo", oldValue: cleanLeadData.assignedTo || null, newValue: selectedUser }]
                }
              ]
            };

            tx.set(newLeadRef, newLeadData);
            tx.set(doc(db, LEAD_ASSIGNMENTS_COLLECTION, newLeadRef.id), {
              id: newLeadRef.id,
              createdAt: new Date().toISOString(),
              sourceMarketLeadId: lead.id,
              recipientLeadId: newLeadRef.id,
              recipientUserId: selectedUser,
              assignmentType: "selected",
              assignedByUserId: currentUser.id,
              paid: false,
              sellerId: lead.sellerId,
              sellerName: lead.sellerName,
              tenantId: data.tenantId || LEGACY_TENANT_ID,
              folderName: lead.folderName,
              sourceLeadId: lead.leadId,
              cleanupStatus: "active",
            });
            tx.delete(doc(db, MARKET_LEADS_COLLECTION, lead.id));
            if (lead.type === "old-contact" && lead.leadId) {
              tx.delete(doc(db, LEADS_COLLECTION, lead.leadId));
            }
            chunkAssigned.push(lead.id);
          }
        });

        assignedIds.push(...chunkAssigned);
        skippedIds.push(...chunkSkipped);
      }

      cache.invalidate(`marketLeads:${currentUser?.tenantId || LEGACY_TENANT_ID}`);

      if (skippedIds.length === 0) {
        toast({ title: "Leads zugewiesen", description: `${assignedIds.length} Leads wurden erfolgreich zugewiesen.` });
      } else if (assignedIds.length > 0) {
        toast({
          title: "Teilzuweisung",
          description: `${assignedIds.length} zugewiesen, ${skippedIds.length} bereits anderweitig vergeben.`,
          variant: "destructive",
        });
      } else {
        toast({ title: "Fehler", description: "Alle Zuweisungen sind fehlgeschlagen oder bereits vergeben.", variant: "destructive" });
      }

      setSelectedLeadsForAssignment(new Set());
      setSelectedUser("");
      const assignedSet = new Set(assignedIds);
      setMarketData(prev => ({
        newLeads: prev.newLeads.filter(l => !assignedSet.has(l.id)),
        oldContacts: prev.oldContacts.filter(l => !assignedSet.has(l.id)),
        packages: prev.packages
      }));
    } catch (error) {
      console.error("Error in bulk assign operation:", error);
      const msg = error instanceof Error ? error.message : "Unerwarteter Fehler beim Zuweisen der Leads.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    } finally {
      setIsBulkAssigningSelected(false);
    }
  };

  // Handle creating a new lead order
  const handleCreateOrder = async (packageSize: number) => {
    if (!currentUser || !currentUser.id) {
      toast({
        title: "Nicht eingeloggt",
        description: "Sie müssen eingeloggt sein, um ein individuelles Angebot zu erstellen.",
        variant: "destructive"
      });
      return;
    }

    // Calculate average price from available leads
    const availableLeads = marketData.newLeads.filter(l => l.status === "available");
    if (availableLeads.length === 0) {
      toast({
        title: "Keine Leads verfügbar",
        description: "Es sind aktuell keine Leads zum Kauf verfügbar.",
        variant: "destructive"
      });
      return;
    }

    const averagePrice = availableLeads.reduce((sum, lead) => sum + lead.price, 0) / availableLeads.length;

    const orderId = await createLeadOrder(
      currentUser.id,
      currentUser.name || "Unbekannt",
      packageSize,
      averagePrice,
      undefined,
      currentUser.tenantId
    );

    if (orderId) {
      toast({
        title: "Individuelles Angebot erstellt",
        description: "Ihr individuelles Angebot wurde erfolgreich erstellt und wird von einem Admin geprüft."
      });
      setIsOrderDialogOpen(false);
    }
  };

  // Handle approving a lead order
  const handleApproveOrder = async (order: LeadOrder) => {
    if (!isAdmin) return;

    const success = await updateLeadOrderStatus(order.id, "approved");
    if (success) {
      // Try to automatically assign leads
      const assignmentSuccess = await assignLeadsToOrder(order.id, order);

      if (assignmentSuccess) {
        // On success the service set status="completed" and cleared notes
        setOrders(prev => prev.map(o =>
          o.id === order.id
            ? { ...o, status: "completed", notes: "", updatedAt: new Date().toISOString() }
            : o
        ));
        // Optimistic update: remove leads that were assigned from the market
        // The leads will be filtered by status in the service layer
        setMarketData(prev => ({
          newLeads: prev.newLeads.filter(l => l.status === "available"),
          oldContacts: prev.oldContacts.filter(l => l.status === "available"),
          packages: prev.packages
        }));
      } else {
        // Assignment failed: order stays "approved", failure note was persisted by the service
        const folderHint = order.folderName ? ` für den Ordner "${order.folderName}"` : "";
        const failureNote = `Automatische Zuweisung fehlgeschlagen am ${new Date().toLocaleDateString('de-DE')}: Es konnten nicht genug Leads${folderHint} gefunden werden.`;
        setOrders(prev => prev.map(o =>
          o.id === order.id
            ? { ...o, status: "approved", notes: failureNote, updatedAt: new Date().toISOString() }
            : o
        ));
      }
    }
  };

  // Handle rejecting a lead order
  const handleRejectOrder = async (order: LeadOrder) => {
    if (!isAdmin) return;

    const success = await updateLeadOrderStatus(order.id, "rejected");
    if (success) {
      // Update the order in the local state instead of removing it
      setOrders(prev => prev.map(o =>
        o.id === order.id
          ? { ...o, status: "rejected", updatedAt: new Date().toISOString() }
          : o
      ));
    }
  };

  // Handle listing a lead for sale
  const handleListLeadForSale = async () => {
    if (!currentUser || !currentUser.id) {
      toast({
        title: "Nicht eingeloggt",
        description: "Sie müssen eingeloggt sein, um einen Lead zum Verkauf anzubieten.",
        variant: "destructive"
      });
      return;
    }

    if (!newListingLead || !newListingPrice || !newListingFolder || !newListingDescription) {
      toast({
        title: "Fehlerhafte Eingabe",
        description: "Bitte füllen Sie alle Felder aus.",
        variant: "destructive",
      });
      return;
    }

    const price = parseFloat(newListingPrice);
    if (isNaN(price) || price <= 0) {
      toast({
        title: "Ungültiger Preis",
        description: "Bitte geben Sie einen gültigen Preis ein.",
        variant: "destructive",
      });
      return;
    }

    // List the lead for sale in the database
    const listingId = await createMarketListing(
      newListingLead.id,
      newListingLead,
      price,
      newListingDescription,
      newListingFolder,
      currentUser.name || "Unbekannt",
      currentUser.id
    );

    if (listingId) {
      // Add the new listing to the local state
      const newMarketLead: MarketLead = {
        id: listingId,
        leadId: newListingLead.id,
        leadData: newListingLead,
        price,
        description: newListingDescription,
        folderName: newListingFolder,
        sellerName: currentUser.name || "Unbekannt",
        sellerId: currentUser.id,
        listedDate: new Date().toISOString(),
        isAvailable: true,
        type: newListingLead.status === "old-contact" ? "old-contact" : "new",
        status: "available",
        followUpDate: newListingLead.status === "old-contact" && newListingLead.nextContactDate ? new Date(newListingLead.nextContactDate).toISOString() : undefined
      };

      // Update marketData instead of marketLeads
      setMarketData(prev => ({
        ...prev,
        newLeads: [newMarketLead, ...prev.newLeads]
      }));

      // Reset form
      setNewListingLead(null);
      setNewListingPrice("");
      setNewListingFolder("");
      setNewListingDescription("");
      setIsDialogOpen(false);

      toast({
        title: "Lead eingestellt",
        description: "Der Lead wurde erfolgreich zum Verkauf eingestellt."
      });
    }
  };

  // Handle adding leads to marketplace
  const handleAddLeadsToMarketplace = async () => {
    if (!currentUser || selectedLeads.length === 0) return;

    const price = parseFloat(bulkPrice);
    if (isNaN(price) || price <= 0) {
      toast({
        title: "Ungültiger Preis",
        description: "Bitte geben Sie einen gültigen Preis ein.",
        variant: "destructive"
      });
      return;
    }

    if (!bulkFolder || !bulkDescription) {
      toast({
        title: "Fehlende Informationen",
        description: "Bitte füllen Sie alle erforderlichen Felder aus.",
        variant: "destructive"
      });
      return;
    }

    const success = await addLeadsToMarketplace(
      selectedLeads,
      price,
      bulkFolder,
      bulkDescription,
      currentUser.id,
      currentUser.name || "Unbekannt"
    );

    if (success) {
      // Optimistic update: add new listings to market data
      const newMarketLeads = selectedLeads.map(lead => ({
        id: `${lead.id}-${Date.now()}`,
        leadData: lead,
        type: "new" as const,
        price: price,
        folderName: bulkFolder,
        description: bulkDescription,
        sellerName: currentUser.name || "Unbekannt",
        sellerId: currentUser.id,
        status: "available" as const,
        createdAt: new Date().toISOString(),
        listedDate: new Date().toISOString()
      }));

      setMarketData(prev => ({
        newLeads: [...prev.newLeads, ...newMarketLeads],
        oldContacts: prev.oldContacts,
        packages: prev.packages
      }));

      // Reset form
      setSelectedLeads([]);
      setBulkPrice("");
      setBulkFolder("");
      setBulkDescription("");
      setIsAddLeadsDialogOpen(false);
    }
  };

  // Handle CSV import
  const handleImportLeads = async (importedLeads: Partial<Lead>[]) => {
    if (!currentUser) return;

    const price = parseFloat(importPrice);
    if (isNaN(price) || price <= 0) {
      toast({
        title: "Ungültiger Preis",
        description: "Bitte geben Sie einen gültigen Preis ein.",
        variant: "destructive"
      });
      return;
    }

    if (!importFolder) {
      toast({
        title: "Fehlende Informationen",
        description: "Bitte wählen Sie einen Ordner aus.",
        variant: "destructive"
      });
      return;
    }

    const success = await addLeadsToMarketplace(
      importedLeads as Lead[],
      price,
      importFolder,
      importDescription,
      currentUser.id,
      currentUser.name || "Unbekannt"
    );

    if (success) {
      // Optimistic update: add imported leads to market data
      const importedMarketLeads = (importedLeads as Lead[]).map((lead, index) => ({
        id: `${lead.id}-${Date.now()}-${index}`,
        leadData: lead,
        type: "new" as const,
        price: price,
        folderName: importFolder,
        description: importDescription,
        sellerName: currentUser.name || "Unbekannt",
        sellerId: currentUser.id,
        status: "available" as const,
        createdAt: new Date().toISOString(),
        listedDate: new Date().toISOString()
      }));

      setMarketData(prev => ({
        newLeads: [...prev.newLeads, ...importedMarketLeads],
        oldContacts: prev.oldContacts,
        packages: prev.packages
      }));

      // Reset form
      setImportPrice("");
      setImportFolder("");
      setImportDescription("");
      setIsImportOpen(false);

      toast({
        title: "Leads importiert",
        description: `${importedLeads.length} Leads wurden erfolgreich zur Börse hinzugefügt.`
      });
    }
  };

  // Remove the old handleAddFolder function and keep only the new one
  const handleAddFolder = async (newFolderName: string) => {
    if (!newFolderName.trim()) return;

    // Check if folder already exists
    if (folders.includes(newFolderName.trim())) {
      toast({
        title: "Ordner existiert bereits",
        description: "Ein Ordner mit diesem Namen existiert bereits.",
        variant: "destructive"
      });
      return;
    }

    // Add to folders list
    setFolders(prev => [...prev, newFolderName.trim()]);

    // If we're in the folder management dialog, update the editing state
    if (editingFolder?.oldName === "Neuer Ordner") {
      setEditingFolder({ oldName: newFolderName.trim(), newName: newFolderName.trim() });
    }

    toast({
      title: "Neuer Ordner erstellt",
      description: `Der Ordner "${newFolderName.trim()}" wurde erstellt.`
    });
  };

  // Add function to handle folder renaming
  const handleRenameFolder = async (oldName: string, newName: string) => {
    if (!newName.trim() || oldName === newName) return;

    try {
      // Update all leads in the folder
      const batch = writeBatch(db);
      const leadsToUpdate = marketData.newLeads.filter(lead => lead.folderName === oldName);

      leadsToUpdate.forEach(lead => {
        const leadRef = doc(db, MARKET_LEADS_COLLECTION, lead.id);
        batch.update(leadRef, { folderName: newName });
      });

      await batch.commit();

      // Update local state
      setMarketData(prev => ({
        ...prev,
        newLeads: prev.newLeads.map(lead =>
          lead.folderName === oldName
            ? { ...lead, folderName: newName }
            : lead
        )
      }));

      // Update folders list
      setFolders(prev => prev.map(f => f === oldName ? newName : f));

      toast({
        title: "Ordner umbenannt",
        description: `Der Ordner "${oldName}" wurde zu "${newName}" umbenannt.`
      });
    } catch (error) {
      console.error("Error renaming folder:", error);
      toast({
        title: "Fehler beim Umbenennen",
        description: "Der Ordner konnte nicht umbenannt werden.",
        variant: "destructive"
      });
    }
  };

  // Add function to handle folder deletion
  const handleDeleteFolder = async (folderName: string) => {
    try {
      // First, move all leads to "Unsortiert" folder
      const batch = writeBatch(db);
      const leadsToUpdate = marketData.newLeads.filter(lead => lead.folderName === folderName);

      leadsToUpdate.forEach(lead => {
        const leadRef = doc(db, MARKET_LEADS_COLLECTION, lead.id);
        batch.update(leadRef, { folderName: "Unsortiert" });
      });

      await batch.commit();

      // Update local state
      setMarketData(prev => ({
        ...prev,
        newLeads: prev.newLeads.map(lead =>
          lead.folderName === folderName
            ? { ...lead, folderName: "Unsortiert" }
            : lead
        )
      }));

      // Update folders list
      setFolders(prev => {
        const newFolders = prev.filter(f => f !== folderName);
        if (!newFolders.includes("Unsortiert")) {
          newFolders.push("Unsortiert");
        }
        return newFolders;
      });

      toast({
        title: "Ordner gelöscht",
        description: `Die Leads aus dem Ordner "${folderName}" wurden in "Unsortiert" verschoben.`
      });
    } catch (error) {
      console.error("Error deleting folder:", error);
      toast({
        title: "Fehler beim Löschen",
        description: "Der Ordner konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    }
  };

  // Add function to handle moving leads between folders
  const handleMoveLeads = async (fromFolder: string, toFolder: string, leads: MarketLead[]) => {
    try {
      const batch = writeBatch(db);

      leads.forEach(lead => {
        const leadRef = doc(db, MARKET_LEADS_COLLECTION, lead.id);
        batch.update(leadRef, { folderName: toFolder });
      });

      await batch.commit();

      // Update local state
      setMarketData(prev => ({
        ...prev,
        newLeads: prev.newLeads.map(lead =>
          leads.some(l => l.id === lead.id)
            ? { ...lead, folderName: toFolder }
            : lead
        )
      }));

      // Update folders list if new folder doesn't exist
      if (!folders.includes(toFolder)) {
        setFolders(prev => [...prev, toFolder]);
      }

      toast({
        title: "Leads verschoben",
        description: `${leads.length} Leads wurden in den Ordner "${toFolder}" verschoben.`
      });
    } catch (error) {
      console.error("Error moving leads:", error);
      toast({
        title: "Fehler beim Verschieben",
        description: "Die Leads konnten nicht verschoben werden.",
        variant: "destructive"
      });
    }
  };

  // Add function to handle moving a single lead
  const handleMoveLead = async (lead: MarketLead, newFolder: string) => {
    try {
      const leadRef = doc(db, MARKET_LEADS_COLLECTION, lead.id);
      await updateDoc(leadRef, { folderName: newFolder });

      // Update local state
      setMarketData(prev => ({
        ...prev,
        newLeads: prev.newLeads.map(l =>
          l.id === lead.id ? { ...l, folderName: newFolder } : l
        )
      }));

      // Update folders list if new folder doesn't exist
      if (!folders.includes(newFolder)) {
        setFolders(prev => [...prev, newFolder]);
      }

      toast({
        title: "Lead verschoben",
        description: `Der Lead wurde in den Ordner "${newFolder}" verschoben.`
      });
    } catch (error) {
      console.error("Error moving lead:", error);
      toast({
        title: "Fehler beim Verschieben",
        description: "Der Lead konnte nicht verschoben werden.",
        variant: "destructive"
      });
    }
  };

  // Add function to handle lead assignment (transactional, race-safe)
  const handleAssignLead = async (lead: MarketLead, userId: string) => {
    if (!currentUser?.id) { console.log("No current user found"); return; }
    if (isAssigningLead) return;
    setIsAssigningLead(true);

    try {
      const currentDate = new Date().toLocaleDateString('de-DE');
      const marketplacePurchaseInfo = lead.type === "old-contact"
        ? `Über Marktplatz zugewiesen von System am ${currentDate}`
        : `Über Marktplatz zugewiesen von ${lead.sellerName} am ${currentDate}`;

      const cleanLeadData = Object.fromEntries(
        Object.entries(lead.leadData).filter(([_, value]) => value !== undefined)
      );

      const marketLeadRef = doc(db, MARKET_LEADS_COLLECTION, lead.id);
      const newLeadRef = doc(collection(db, LEADS_COLLECTION));

      await runTransaction(db, async (tx) => {
        // Re-read marketLead inside transaction to prevent concurrent claims.
        const snap = await tx.get(marketLeadRef);
        if (!snap.exists()) throw new Error("Lead nicht mehr verfügbar (bereits zugewiesen).");
        const data = snap.data() as MarketLead;
        if (data.status !== "available") throw new Error("Lead ist nicht mehr verfügbar.");

        const newLeadData = {
          ...cleanLeadData,
          id: newLeadRef.id,
          assignedTo: userId,
          status: lead.type === "old-contact" ? "old-contact" : "new",
          createdAt: cleanLeadData.createdAt || new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          nextContactDate: lead.type === "old-contact" ? getDateValue(cleanLeadData.nextContactDate) : null,
          source: "market",
          marketLeadId: lead.id,
          marketplacePurchaseInfo,
          tenantId: data.tenantId || LEGACY_TENANT_ID,
          folderId: null,
          history: [
            ...(cleanLeadData.history || []),
            {
              type: "marketplace_assignment",
              date: new Date().toISOString(),
              user: currentUser.id,
              changes: [{ field: "assignedTo", oldValue: cleanLeadData.assignedTo || null, newValue: userId }]
            }
          ]
        };

        tx.set(newLeadRef, newLeadData);
        tx.set(doc(db, LEAD_ASSIGNMENTS_COLLECTION, newLeadRef.id), {
          id: newLeadRef.id,
          createdAt: new Date().toISOString(),
          sourceMarketLeadId: lead.id,
          recipientLeadId: newLeadRef.id,
          recipientUserId: userId,
          assignmentType: "direct",
          assignedByUserId: currentUser.id,
          paid: false,
          sellerId: lead.sellerId,
          sellerName: lead.sellerName,
          tenantId: data.tenantId || LEGACY_TENANT_ID,
          folderName: lead.folderName,
          sourceLeadId: lead.leadId,
          cleanupStatus: "active",
        });
        tx.delete(marketLeadRef);

        if (lead.type === "old-contact" && lead.leadId) {
          tx.delete(doc(db, LEADS_COLLECTION, lead.leadId));
        }
      });

      cache.invalidate(`marketLeads:${currentUser?.tenantId || LEGACY_TENANT_ID}`);

      setMarketData(prev => ({
        newLeads: prev.newLeads.filter(l => l.id !== lead.id),
        oldContacts: prev.oldContacts.filter(l => l.id !== lead.id),
        packages: prev.packages
      }));

      toast({ title: "Lead zugewiesen", description: "Der Lead wurde erfolgreich zugewiesen und aus der Börse entfernt." });
      setAssigningLead(null);
      setSelectedUser("");
    } catch (error) {
      console.error("Error assigning lead:", error);
      const msg = error instanceof Error ? error.message : "Der Lead konnte nicht zugewiesen werden.";
      toast({ title: "Fehler bei der Zuweisung", description: msg, variant: "destructive" });
    } finally {
      setIsAssigningLead(false);
    }
  };

  // Add function to handle lead transfer to admin
  const handleTransferLead = async (lead: MarketLead) => {
    if (!currentUser?.id || (currentUser.role !== "admin" && currentUser.role !== "developer")) return;

    try {
      const success = await transferLead(lead.leadId, currentUser.id);
      if (success) {
        // Update local state
        setMarketData(prev => ({
          ...prev,
          oldContacts: prev.oldContacts.map(l =>
            l.id === lead.id
              ? { ...l, assignedTo: currentUser.id, status: "assigned" }
              : l
          )
        }));

        toast({
          title: "Lead übernommen",
          description: "Der Lead wurde erfolgreich übernommen."
        });
      }
    } catch (error) {
      console.error("Error transferring lead:", error);
      toast({
        title: "Fehler bei der Übernahme",
        description: "Der Lead konnte nicht übernommen werden.",
        variant: "destructive"
      });
    }
  };

  // Bulk assignment of all leads in a folder (transactional, chunked, race-safe)
  const handleBulkAssignLeads = async (folder: string, leads: MarketLead[], userId: string) => {
    if (!currentUser?.id) { console.log("No current user found"); return; }
    if (isBulkAssigning) return;
    setIsBulkAssigning(true);

    const CHUNK = 100; // ≈ 300 ops per tx (read + set + delete)
    const currentDate = new Date().toLocaleDateString('de-DE');
    const assignedIds: string[] = [];
    const skippedIds: string[] = [];

    const assignmentType = "bulk";
    try {
      for (let start = 0; start < leads.length; start += CHUNK) {
        const chunk = leads.slice(start, start + CHUNK);
        // Retry-safe accumulators: re-init inside callback, only commit after success.
        let chunkAssigned: string[] = [];
        let chunkSkipped: string[] = [];

        await runTransaction(db, async (tx) => {
          chunkAssigned = [];
          chunkSkipped = [];

          const reads = await Promise.all(
            chunk.map(lead => tx.get(doc(db, MARKET_LEADS_COLLECTION, lead.id)))
          );

          for (let i = 0; i < chunk.length; i++) {
            const lead = chunk[i];
            const snap = reads[i];
            if (!snap.exists()) { chunkSkipped.push(lead.id); continue; }
            const data = snap.data() as MarketLead;
            if (data.status !== "available") { chunkSkipped.push(lead.id); continue; }

            const marketplacePurchaseInfo = lead.type === "old-contact"
              ? `Über Marktplatz zugewiesen von System am ${currentDate}`
              : `Über Marktplatz zugewiesen von ${lead.sellerName} am ${currentDate}`;

            const newLeadRef = doc(collection(db, LEADS_COLLECTION));
            const newLeadData = {
              ...lead.leadData,
              id: newLeadRef.id,
              assignedTo: userId,
              status: lead.type === "old-contact" ? "old-contact" : "new",
              createdAt: lead.leadData.createdAt || new Date().toISOString(),
              lastUpdated: new Date().toISOString(),
              nextContactDate: lead.type === "old-contact" ? getDateValue(lead.leadData.nextContactDate) : null,
              source: "market",
              marketLeadId: lead.id,
              marketplacePurchaseInfo,
              tenantId: data.tenantId || LEGACY_TENANT_ID,
              folderId: selectedFolderId || null,
              history: [
                ...(lead.leadData.history || []),
                {
                  type: "marketplace_assignment",
                  date: new Date().toISOString(),
                  user: currentUser.id,
                  changes: [{ field: "assignedTo", oldValue: lead.leadData.assignedTo || null, newValue: userId }]
                }
              ]
            };

            tx.set(newLeadRef, newLeadData);
            tx.set(doc(db, LEAD_ASSIGNMENTS_COLLECTION, newLeadRef.id), {
              id: newLeadRef.id,
              createdAt: new Date().toISOString(),
              sourceMarketLeadId: lead.id,
              recipientLeadId: newLeadRef.id,
              recipientUserId: userId,
              assignmentType,
              assignedByUserId: currentUser.id,
              paid: false,
              sellerId: lead.sellerId,
              sellerName: lead.sellerName,
              tenantId: data.tenantId || LEGACY_TENANT_ID,
              folderName: folder,
              sourceLeadId: lead.leadId,
              cleanupStatus: "active",
            });
            tx.delete(doc(db, MARKET_LEADS_COLLECTION, lead.id));
            if (lead.type === "old-contact" && lead.leadId) {
              tx.delete(doc(db, LEADS_COLLECTION, lead.leadId));
            }
            chunkAssigned.push(lead.id);
          }
        });

        assignedIds.push(...chunkAssigned);
        skippedIds.push(...chunkSkipped);
      }

      cache.invalidate(`marketLeads:${currentUser?.tenantId || LEGACY_TENANT_ID}`);

      setMarketData(prev => ({
        newLeads: prev.newLeads.filter(l => !assignedIds.includes(l.id)),
        oldContacts: prev.oldContacts.filter(l => !assignedIds.includes(l.id)),
        packages: prev.packages
      }));

      if (skippedIds.length === 0) {
        toast({ title: "Leads zugewiesen", description: `${assignedIds.length} Leads wurden erfolgreich zugewiesen.` });
      } else {
        toast({
          title: "Teilzuweisung",
          description: `${assignedIds.length} zugewiesen, ${skippedIds.length} bereits anderweitig vergeben.`,
          variant: "destructive",
        });
      }

      setBulkAssigningFolder(null);
      setSelectedUser("");
    } catch (error) {
      console.error("Error assigning leads:", error);
      const msg = error instanceof Error ? error.message : "Die Leads konnten nicht zugewiesen werden.";
      toast({ title: "Fehler bei der Zuweisung", description: msg, variant: "destructive" });
    } finally {
      setIsBulkAssigning(false);
    }
  };

  // Add function to handle single lead deletion
  const handleDeleteLead = async (lead: MarketLead) => {
    try {
      const leadRef = doc(db, MARKET_LEADS_COLLECTION, lead.id);
      await deleteDoc(leadRef);

      // Update local state
      setMarketData(prev => ({
        ...prev,
        newLeads: prev.newLeads.filter(l => l.id !== lead.id)
      }));

      toast({
        title: "Lead gelöscht",
        description: "Der Lead wurde erfolgreich gelöscht."
      });
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast({
        title: "Fehler beim Löschen",
        description: "Der Lead konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    }
  };

  // Add function to handle bulk lead deletion
  const handleBulkDeleteLeads = async (folder: string, leads: MarketLead[]) => {
    try {
      const batch = writeBatch(db);

      leads.forEach(lead => {
        const leadRef = doc(db, MARKET_LEADS_COLLECTION, lead.id);
        batch.delete(leadRef);
      });

      await batch.commit();

      // Update local state
      setMarketData(prev => ({
        ...prev,
        newLeads: prev.newLeads.filter(lead => !leads.some(l => l.id === lead.id))
      }));

      toast({
        title: "Leads gelöscht",
        description: `${leads.length} Leads wurden erfolgreich gelöscht.`
      });
    } catch (error) {
      console.error("Error deleting leads:", error);
      toast({
        title: "Fehler beim Löschen",
        description: "Die Leads konnten nicht gelöscht werden.",
        variant: "destructive"
      });
    }
  };

  // Add new function for bulk transfer
  const handleBulkTransferLeads = async (folder: string, leads: MarketLead[]) => {
    if (!currentUser?.id || (currentUser.role !== "admin" && currentUser.role !== "developer")) return;

    let successCount = 0;
    let errorCount = 0;

    try {
      for (const lead of leads) {
        const success = await transferLead(lead.leadId, currentUser.id);
        if (success) {
          successCount++;
        } else {
          errorCount++;
        }
      }

      // Update local state by marking leads as assigned (transferred to admin)
      setMarketData(prev => ({
        ...prev,
        oldContacts: prev.oldContacts.map(l => {
          if (leads.find(transferredLead => transferredLead.id === l.id)) {
            return { ...l, assignedTo: currentUser.id, status: "assigned" };
          }
          return l;
        })
      }));

      if (errorCount === 0) {
        toast({
          title: "Leads übernommen",
          description: `${successCount} Leads wurden erfolgreich übernommen.`
        });
      } else {
        toast({
          title: "Teilweise erfolgreich",
          description: `${successCount} Leads übernommen, ${errorCount} Fehler aufgetreten.`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error transferring leads:", error);
      toast({
        title: "Fehler bei der Übernahme",
        description: "Die Leads konnten nicht übernommen werden.",
        variant: "destructive"
      });
    }
    setBulkTransferringFolder(null);
  };

  // Add new function for count-based assignment
  const handleCountAssignLeads = async (folder: string, leads: MarketLead[], userId: string, count: number) => {
    if (!currentUser?.id) { console.log("No current user found"); return; }
    if (isAssigningCount) { console.log("Assignment already in progress"); return; }
    setIsAssigningCount(true);

    const CHUNK = 100;
    const currentDate = new Date().toLocaleDateString('de-DE');
    const leadsToAssign = leads.slice(0, count);
    const assignedIds: string[] = [];
    const skippedIds: string[] = [];

    const assignmentType = "count";
    try {
      for (let start = 0; start < leadsToAssign.length; start += CHUNK) {
        const chunk = leadsToAssign.slice(start, start + CHUNK);
        let chunkAssigned: string[] = [];
        let chunkSkipped: string[] = [];

        await runTransaction(db, async (tx) => {
          chunkAssigned = [];
          chunkSkipped = [];

          const reads = await Promise.all(
            chunk.map(lead => tx.get(doc(db, MARKET_LEADS_COLLECTION, lead.id)))
          );

          for (let i = 0; i < chunk.length; i++) {
            const lead = chunk[i];
            const snap = reads[i];
            if (!snap.exists()) { chunkSkipped.push(lead.id); continue; }
            const data = snap.data() as MarketLead;
            if (data.status !== "available") { chunkSkipped.push(lead.id); continue; }

            const marketplacePurchaseInfo = lead.type === "old-contact"
              ? `Über Marktplatz zugewiesen von System am ${currentDate}`
              : `Über Marktplatz zugewiesen von ${lead.sellerName} am ${currentDate}`;

            const newLeadRef = doc(collection(db, LEADS_COLLECTION));
            const newLeadData = {
              ...lead.leadData,
              id: newLeadRef.id,
              assignedTo: userId,
              status: lead.type === "old-contact" ? "old-contact" : "new",
              createdAt: lead.leadData.createdAt || new Date().toISOString(),
              lastUpdated: new Date().toISOString(),
              nextContactDate: lead.type === "old-contact" ? getDateValue(lead.leadData.nextContactDate) : null,
              source: "market",
              marketLeadId: lead.id,
              marketplacePurchaseInfo,
              tenantId: data.tenantId || LEGACY_TENANT_ID,
              folderId: selectedFolderId || null,
              history: [
                ...(lead.leadData.history || []),
                {
                  type: "marketplace_assignment",
                  date: new Date().toISOString(),
                  user: currentUser.id,
                  changes: [{ field: "assignedTo", oldValue: lead.leadData.assignedTo || null, newValue: userId }]
                }
              ]
            };

            tx.set(newLeadRef, newLeadData);
            tx.set(doc(db, LEAD_ASSIGNMENTS_COLLECTION, newLeadRef.id), {
              id: newLeadRef.id,
              createdAt: new Date().toISOString(),
              sourceMarketLeadId: lead.id,
              recipientLeadId: newLeadRef.id,
              recipientUserId: userId,
              assignmentType,
              assignedByUserId: currentUser.id,
              paid: false,
              sellerId: lead.sellerId,
              sellerName: lead.sellerName,
              tenantId: data.tenantId || LEGACY_TENANT_ID,
              folderName: folder,
              sourceLeadId: lead.leadId,
              cleanupStatus: "active",
            });
            tx.delete(doc(db, MARKET_LEADS_COLLECTION, lead.id));
            if (lead.type === "old-contact" && lead.leadId) {
              tx.delete(doc(db, LEADS_COLLECTION, lead.leadId));
            }
            chunkAssigned.push(lead.id);
          }
        });

        assignedIds.push(...chunkAssigned);
        skippedIds.push(...chunkSkipped);
      }

      cache.invalidate(`marketLeads:${currentUser?.tenantId || LEGACY_TENANT_ID}`);

      setMarketData(prev => ({
        newLeads: prev.newLeads.filter(l => !assignedIds.includes(l.id)),
        oldContacts: prev.oldContacts.filter(l => !assignedIds.includes(l.id)),
        packages: prev.packages
      }));

      if (skippedIds.length === 0) {
        toast({ title: "Leads zugewiesen", description: `${assignedIds.length} Leads wurden erfolgreich zugewiesen.` });
      } else {
        toast({
          title: "Teilzuweisung",
          description: `${assignedIds.length} zugewiesen, ${skippedIds.length} bereits anderweitig vergeben.`,
          variant: "destructive",
        });
      }

      setCountAssigningFolder(null);
      setSelectedUser("");
      setAssignCount("");
      setIsCountAssignDialogOpen(false);
    } catch (error) {
      console.error("Error assigning leads:", error);
      toast({
        title: "Fehler bei der Zuweisung",
        description: "Die Leads konnten nicht zugewiesen werden.",
        variant: "destructive"
      });
    } finally {
      // Always reset loading state, even on error
      setIsAssigningCount(false);
    }
  };

  // Handle creating a folder package
  const handleCreateFolderPackage = async (folder: string, leads: MarketLead[]) => {
    if (!currentUser || !currentUser.id) {
      toast({
        title: "Nicht eingeloggt",
        description: "Sie müssen eingeloggt sein, um ein Lead-Paket zu erstellen.",
        variant: "destructive"
      });
      return;
    }

    const description = `Lead-Paket "${folder}" mit ${leads.length} Leads`;
    const packageId = await createFolderPackage(
      folder,
      description,
      currentUser.id,
      currentUser.name || "Unbekannt",
      leads
    );

    if (packageId) {
      // Optimistic update: add new package to market data
      const newPackage: LeadPackage = {
        id: packageId,
        folder: folder,
        description: description,
        leadCount: leads.length,
        createdBy: currentUser.id,
        createdByName: currentUser.name || "Unbekannt",
        createdAt: new Date().toISOString(),
        status: "active",
        leads: leads
      };

      setMarketData(prev => ({
        newLeads: prev.newLeads,
        oldContacts: prev.oldContacts,
        packages: [...prev.packages, newPackage]
      }));

      toast({
        title: "Lead-Paket erstellt",
        description: `Das Paket "${folder}" mit ${leads.length} Leads wurde erfolgreich erstellt.`
      });
    }
  };

  // Simplify the handlePurchasePackage function to always create a lead order
  const handlePurchasePackage = async (folder: string, packageSize: number) => {
    if (!currentUser || !currentUser.id) {
      toast({
        title: "Nicht eingeloggt",
        description: "Sie müssen eingeloggt sein, um Leads zu kaufen.",
        variant: "destructive"
      });
      return;
    }

    // Calculate average price from available leads in the folder
    const availableLeads = marketData.newLeads.filter(l =>
      l.folderName === folder && l.status === "available"
    );
    const avgPrice = availableLeads.length > 0
      ? availableLeads.reduce((sum, lead) => sum + lead.price, 0) / availableLeads.length
      : 0;

    // Create a lead order with folder name
    const orderId = await createLeadOrder(
      currentUser.id,
      currentUser.name || "Unbekannt",
      packageSize,
      avgPrice,
      folder, // Übergebe den Ordnernamen
      currentUser.tenantId
    );

    if (orderId) {
      toast({
        title: "Lead-Anfrage erstellt",
        description: `Ihre Anfrage für ${packageSize} Leads aus dem Ordner "${folder}" wurde erstellt und wird von einem Admin geprüft.`
      });
    }
  };

  // Add function to load available old contacts
  const loadAvailableOldContacts = async () => {
    try {
      const allLeads = await fetchLeadsForCurrentUser(
        currentUser!.id,
        currentUser!.role,
        currentUser!.tenantId
      );
      const oldContacts = allLeads.filter(lead =>
        lead.status === "old-contact" &&
        !marketData.oldContacts.some(marketLead => marketLead.leadId === lead.id)
      );
      setAvailableOldContacts(oldContacts);
    } catch (error) {
      console.error("Error loading available old contacts:", error);
      toast({
        title: "Fehler",
        description: "Die verfügbaren Altkontakte konnten nicht geladen werden.",
        variant: "destructive"
      });
    }
  };

  // Add function to handle adding an old contact
  const handleAddOldContact = async () => {
    if (!selectedOldContact || !currentUser) return;

    try {
      const price = parseFloat(newOldContactPrice) || 0;
      const listingId = await addOldContactToMarketplace(
        selectedOldContact,
        currentUser.id,
        currentUser.name || "Unbekannt",
        price,
        newOldContactDescription,
        newOldContactFolder,
        currentUser.tenantId
      );

      if (listingId) {
        // Optimistic update: add old contact to market data
        const newOldContactListing: MarketLead = {
          id: listingId,
          leadData: selectedOldContact,
          type: "old-contact" as const,
          price: price,
          folderName: newOldContactFolder,
          description: newOldContactDescription,
          sellerName: "System",
          sellerId: currentUser.id,
          status: "available" as const,
          createdAt: new Date().toISOString(),
          listedDate: new Date().toISOString(),
          leadId: selectedOldContact.id
        };

        setMarketData(prev => ({
          newLeads: prev.newLeads,
          oldContacts: [...prev.oldContacts, newOldContactListing],
          packages: prev.packages
        }));

        // Reset form
        setSelectedOldContact(null);
        setNewOldContactPrice("");
        setNewOldContactDescription("");
        setNewOldContactFolder("Altkontakte");
        setIsAddOldContactDialogOpen(false);

        toast({
          title: "Altkontakt hinzugefügt",
          description: "Der Altkontakt wurde erfolgreich zur Leadbörse hinzugefügt."
        });
      }
    } catch (error) {
      console.error("Error adding old contact:", error);
      toast({
        title: "Fehler",
        description: "Der Altkontakt konnte nicht hinzugefügt werden.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="py-4 px-6 border-b">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <div>
              <h1 className="text-2xl font-bold">Lead Börse</h1>
              <p className="text-sm text-muted-foreground">
                {isAdmin
                  ? "Verwalten Sie Leads und Altkontakte"
                  : "Kaufen Sie Leads über die Lead-Börse"}
              </p>
            </div>
            {isAdmin ? (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Lädt...' : 'Aktualisieren'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFolderManagementOpen(true)}
                >
                  <FolderEdit className="h-4 w-4 mr-2" />
                  Ordner verwalten
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsImportOpen(true)}
                >
                  <FileUp className="h-4 w-4 mr-2" />
                  CSV Import
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setIsOrderDialogOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Individuales angebot
                </Button>
              </div>
            )}
          </div>

          {isAdmin && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
              <TabsList>
                <TabsTrigger value="new-leads">
                  <Users className="h-4 w-4 mr-2" />
                  Neue Leads
                </TabsTrigger>
                <TabsTrigger value="old-contacts">
                  <Clock className="h-4 w-4 mr-2" />
                  Altkontakte
                </TabsTrigger>
                <TabsTrigger value="orders">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Lead-Anfragen
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p>Daten werden geladen...</p>
            </div>
          ) : isAdmin ? (
            <>
              {activeTab === "orders" ? (
                // Orders view
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {orders.map((order) => (
                    <Card key={order.id} className={cn(
                      order.status === "approved" && "border-green-200 bg-green-50/50",
                      order.status === "rejected" && "border-red-200 bg-red-50/50",
                      order.status === "completed" && "border-blue-200 bg-blue-50/50"
                    )}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Lead-Anfrage</CardTitle>
                          <Badge variant={
                            order.status === "approved" ? "default" : // Changed from success to default
                              order.status === "rejected" ? "destructive" :
                                order.status === "completed" ? "secondary" : // Changed to secondary
                                  "outline" // Changed to outline for pending
                          } className={
                            order.status === "approved" ? "bg-green-500 hover:bg-green-600" : "" // Add custom class for green color
                          }>
                            {order.status === "approved" ? "Genehmigt" :
                              order.status === "rejected" ? "Abgelehnt" :
                                order.status === "completed" ? "Abgeschlossen" :
                                  "Ausstehend"}
                          </Badge>
                        </div>
                        <CardDescription>
                          Von {order.userName}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Anzahl:</span>
                            <span className="font-medium">{order.requestedAmount}</span>
                          </div>
                          {order.folderName && (
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Ordner:</span>
                              <span className="font-medium">{order.folderName}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Durchschnittspreis:</span>
                            <span className="font-medium">{order.averagePrice.toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Gesamtpreis:</span>
                            <span className="font-medium">{order.totalPrice.toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Erstellt am:</span>
                            <span className="font-medium">
                              {new Date(order.createdAt).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                          {order.updatedAt && order.updatedAt !== order.createdAt && (
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Aktualisiert am:</span>
                              <span className="font-medium">
                                {new Date(order.updatedAt).toLocaleDateString('de-DE')}
                              </span>
                            </div>
                          )}
                          {order.notes && (
                            <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-800">
                              <span className="font-semibold">Hinweis: </span>
                              {order.notes}
                            </div>
                          )}
                        </div>
                      </CardContent>
                      {order.status === "pending" && (
                        <CardFooter className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => handleRejectOrder(order)}
                          >
                            Ablehnen
                          </Button>
                          <Button
                            onClick={() => handleApproveOrder(order)}
                          >
                            Genehmigen
                          </Button>
                        </CardFooter>
                      )}
                    </Card>
                  ))}
                  {orders.length === 0 && (
                    <div className="col-span-full text-center py-8">
                      <p className="text-muted-foreground">Keine Lead-Anfragen vorhanden</p>
                    </div>
                  )}
                </div>
              ) : activeTab === "new-leads" ? (
                // New leads table view
                <div className="space-y-4">
                  <Accordion type="multiple" className="w-full">
                    {Object.entries(groupedNewLeads).map(([folder, leads]) => (
                      <AccordionItem key={folder} value={folder}>
                        <div className="flex items-center justify-between">
                          <AccordionTrigger className="hover:no-underline flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{folder}</span>
                              <Badge variant="secondary">{leads.length} Leads</Badge>
                            </div>
                          </AccordionTrigger>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setBulkAssigningFolder({ folder, leads });
                              }}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              Alle zuweisen
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCountAssigningFolder({ folder, leads });
                                setSelectedUser("");
                                setAssignCount("");
                                setIsCountAssignDialogOpen(true);
                              }}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              Anzahl zuweisen
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletingFolder({ folder, leads });
                              }}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Alle löschen
                            </Button>
                          </div>
                        </div>
                        <AccordionContent>
                          <div className="bg-white rounded-md shadow">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="cursor-pointer w-24" onClick={() => handleSort("status")}>
                                    <div className="flex items-center justify-between">
                                      Status
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "status" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-32" onClick={() => handleSort("name")}>
                                    <div className="flex items-center justify-between">
                                      Name
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "name" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-28" onClick={() => handleSort("profession")}>
                                    <div className="flex items-center justify-between">
                                      Beruf
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "profession" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-40" onClick={() => handleSort("email")}>
                                    <div className="flex items-center justify-between">
                                      E-Mail
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "email" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-32" onClick={() => handleSort("phone")}>
                                    <div className="flex items-center justify-between">
                                      Telefon
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "phone" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-24" onClick={() => handleSort("priority")}>
                                    <div className="flex items-center justify-between">
                                      Priorität
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "priority" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-32" onClick={() => handleSort("nextContactDate")}>
                                    <div className="flex items-center justify-between">
                                      Nächster Kontakt
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "nextContactDate" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="w-24">Zugewiesen</TableHead>
                                  <TableHead className="w-20">Preis</TableHead>
                                  <TableHead className="w-16"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {leads.map((lead) => {
                                  const leadData = lead.leadData;
                                  const assignedUserName = users.find(u => u.id === leadData.assignedTo)?.name || "...";

                                  return (
                                    <TableRow
                                      key={lead.id}
                                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                      <TableCell className="p-2 pl-4">
                                        <div className="flex items-center">
                                          <div className={cn("w-2 h-2 rounded-full mr-2",
                                            lead.status === "available" ? "bg-green-500" : "bg-gray-400"
                                          )}></div>
                                          <span className="text-sm text-gray-600">
                                            {lead.status === "available" ? "Verfügbar" :
                                              lead.status === "sold" ? "Verkauft" :
                                                lead.status === "pending" ? "In Bearbeitung" : lead.status}
                                          </span>
                                        </div>
                                      </TableCell>
                                      <TableCell className="p-2">
                                        <div className="flex items-center">
                                          <span className="font-medium">
                                            {leadData.firstName} {leadData.lastName}
                                          </span>
                                          {lead.type === "old-contact" && (
                                            <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">
                                              <Clock className="h-3 w-3 mr-1" />
                                              Altkontakt
                                            </Badge>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell className="p-2">{leadData.position || "-"}</TableCell>
                                      <TableCell className="p-2">{leadData.email || "-"}</TableCell>
                                      <TableCell className="p-2">{leadData.phone || "-"}</TableCell>
                                      <TableCell className="p-2">
                                        <Badge
                                          variant="outline"
                                          className={cn(
                                            "font-normal",
                                            leadData.priority === "high"
                                              ? "bg-red-50 text-red-700 border-red-200"
                                              : leadData.priority === "medium"
                                                ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                                : "bg-blue-50 text-blue-700 border-blue-200"
                                          )}
                                        >
                                          {leadData.priority === "high"
                                            ? "Hoch"
                                            : leadData.priority === "medium"
                                              ? "Mittel"
                                              : "Niedrig"}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="p-2">
                                        {(() => {
                                          const dateValue = getDateValue(leadData.nextContactDate);
                                          return dateValue
                                            ? (new Date(dateValue).getTime() ? new Date(dateValue).toLocaleDateString('de-DE') : "-")
                                            : "-";
                                        })()}
                                      </TableCell>
                                      <TableCell className="p-2">
                                        <div className="flex items-center">
                                          <Users className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                                          <span className="text-gray-600 text-sm">{assignedUserName}</span>
                                        </div>
                                      </TableCell>
                                      <TableCell className="p-2">
                                        <Badge variant="outline" className="font-normal">
                                          {lead.price} €
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="p-2 flex justify-end">
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                              <MoreHorizontal className="h-4 w-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            <DropdownMenuLabel>Aktionen</DropdownMenuLabel>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={() => setMovingLead(lead)}>
                                              <Move className="h-4 w-4 mr-2" />
                                              In anderen Ordner verschieben
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                              onSelect={(e) => {
                                                e.preventDefault();
                                                setAssigningLead(lead);
                                              }}
                                            >
                                              <Users className="h-4 w-4 mr-2" />
                                              Zuweisen
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                              className="text-destructive focus:text-destructive"
                                              onSelect={(e) => {
                                                e.preventDefault();
                                                setDeletingLead(lead);
                                              }}
                                            >
                                              <Trash2 className="h-4 w-4 mr-2" />
                                              Löschen
                                            </DropdownMenuItem>
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  {Object.keys(groupedNewLeads).length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">Keine neuen Leads verfügbar</p>
                    </div>
                  )}
                </div>
              ) : (
                // Old contacts table view
                <div className="space-y-4">
                  {/* Next contact date filtering controls */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-md">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium">Nächster Kontakt-Filter:</label>
                      <Select value={followUpFilter} onValueChange={(value: "all" | "future" | "overdue") => setFollowUpFilter(value)}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle</SelectItem>
                          <SelectItem value="future">Zukünftig</SelectItem>
                          <SelectItem value="overdue">Überfällig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        id="onlyWithFollowUp"
                        type="checkbox"
                        checked={showOnlyLeadsWithFollowUp}
                        onChange={(e) => setShowOnlyLeadsWithFollowUp(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor="onlyWithFollowUp" className="text-sm">
                        Nur Leads mit nächstem Kontakt-Datum
                      </label>
                    </div>

                    {selectedLeadsForAssignment.size > 0 && (
                      <div className="flex items-center gap-2 ml-auto">
                        <span className="text-sm text-gray-600">
                          {selectedLeadsForAssignment.size} Lead(s) ausgewählt
                        </span>
                        <Select value={selectedUser} onValueChange={setSelectedUser}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Berater wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {visibleUsers.map(user => (
                              <SelectItem
                                key={user.id}
                                value={user.id}
                                className={!user.active ? "text-muted-foreground opacity-60" : ""}
                              >
                                {user.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={handleBulkAssignSelectedLeads}
                          disabled={!selectedUser || isBulkAssigningSelected}
                          size="sm"
                        >
                          <Users className="h-4 w-4 mr-2" />
                          {isBulkAssigningSelected ? "Zuweisen..." : "Zuweisen"}
                        </Button>
                        <Button
                          onClick={() => setSelectedLeadsForAssignment(new Set())}
                          variant="outline"
                          size="sm"
                        >
                          Auswahl aufheben
                        </Button>
                      </div>
                    )}
                  </div>
                  <Accordion type="multiple" className="w-full">
                    {Object.entries(groupedOldContacts).map(([folder, contacts]) => (
                      <AccordionItem key={folder} value={folder}>
                        <div className="flex items-center justify-between">
                          <AccordionTrigger className="hover:no-underline flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">System</span>
                              <Badge variant="secondary">{contacts.length} Altkontakte</Badge>
                            </div>
                          </AccordionTrigger>
                          <div className="flex gap-2 pr-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setBulkAssigningFolder({ folder: "System", leads: contacts });
                              }}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              Alle zuweisen
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCountAssigningFolder({ folder: "System", leads: contacts });
                                setSelectedUser("");
                                setAssignCount("");
                                setIsCountAssignDialogOpen(true);
                              }}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              Anzahl zuweisen
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBulkTransferringFolder({ folder: "System", leads: contacts });
                                }}
                              >
                                <ArrowRight className="h-4 w-4 mr-2" />
                                Alle Übernehmen
                              </Button>
                            )}
                          </div>
                        </div>
                        <AccordionContent>
                          <div className="bg-white rounded-md shadow">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-12">
                                    <input
                                      type="checkbox"
                                      checked={contacts.every(contact => selectedLeadsForAssignment.has(contact.id)) && contacts.length > 0}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          const newSelection = new Set(selectedLeadsForAssignment);
                                          contacts.forEach(contact => newSelection.add(contact.id));
                                          setSelectedLeadsForAssignment(newSelection);
                                        } else {
                                          const newSelection = new Set(selectedLeadsForAssignment);
                                          contacts.forEach(contact => newSelection.delete(contact.id));
                                          setSelectedLeadsForAssignment(newSelection);
                                        }
                                      }}
                                      className="rounded border-gray-300"
                                    />
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-24" onClick={() => handleSort("status")}>
                                    <div className="flex items-center justify-between">
                                      Status
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "status" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-32" onClick={() => handleSort("name")}>
                                    <div className="flex items-center justify-between">
                                      Name
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "name" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-28" onClick={() => handleSort("profession")}>
                                    <div className="flex items-center justify-between">
                                      Beruf
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "profession" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-40" onClick={() => handleSort("email")}>
                                    <div className="flex items-center justify-between">
                                      E-Mail
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "email" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-32" onClick={() => handleSort("phone")}>
                                    <div className="flex items-center justify-between">
                                      Telefon
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "phone" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-24" onClick={() => handleSort("priority")}>
                                    <div className="flex items-center justify-between">
                                      Priorität
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "priority" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="cursor-pointer w-32" onClick={() => handleSort("nextContactDate")}>
                                    <div className="flex items-center justify-between">
                                      Nächster Kontakt
                                      <ArrowUpDown className={`h-4 w-4 ${sortField === "nextContactDate" ? "text-blue-600" : "text-gray-400"}`} />
                                    </div>
                                  </TableHead>
                                  <TableHead className="w-24">Zugewiesen</TableHead>
                                  <TableHead className="w-16"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {contacts.map((contact) => {
                                  const leadData = contact.leadData;
                                  const assignedUserName = users.find(u => u.id === leadData.assignedTo)?.name || "...";

                                  return (
                                    <TableRow
                                      key={contact.id}
                                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                      <TableCell className="p-2 pl-4">
                                        <input
                                          type="checkbox"
                                          checked={selectedLeadsForAssignment.has(contact.id)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleLeadSelection(contact.id, e.target.checked);
                                          }}
                                          className="rounded border-gray-300"
                                        />
                                      </TableCell>
                                      <TableCell className="p-2">
                                        <div className="flex items-center">
                                          <div className={cn("w-2 h-2 rounded-full mr-2",
                                            leadData.status === "old-contact" ? "bg-gray-400" : "bg-blue-500"
                                          )}></div>
                                          <span className="text-sm text-gray-600">
                                            {leadData.status === "old-contact" ? "Altkontakt" :
                                              leadData.status === "new" ? "Neu" :
                                                leadData.status === "in-progress" ? "In Bearbeitung" :
                                                  leadData.status === "won" ? "Gewonnen" :
                                                    leadData.status === "lost" ? "Verloren" :
                                                      leadData.status === "wrong-data" ? "Falsche Daten" :
                                                        leadData.status === "delete-data" ? "Löschen" :
                                                          leadData.status === "not-reached" ? "Nicht erreicht" :
                                                            leadData.status === "follow-up" ? "Wiedervorlage" :
                                                              leadData.status === "callback" ? "Rückruf" :
                                                                leadData.status === "scheduled" ? "Terminiert" :
                                                                  leadData.status}
                                          </span>
                                        </div>
                                      </TableCell>
                                      <TableCell className="p-2">
                                        <div className="flex items-center">
                                          <span className="font-medium">
                                            {leadData.firstName} {leadData.lastName}
                                          </span>
                                        </div>
                                      </TableCell>
                                      <TableCell className="p-2">{leadData.position || "-"}</TableCell>
                                      <TableCell className="p-2">{leadData.email || "-"}</TableCell>
                                      <TableCell className="p-2">{leadData.phone || "-"}</TableCell>
                                      <TableCell className="p-2">
                                        <Badge
                                          variant="outline"
                                          className={cn(
                                            "font-normal",
                                            leadData.priority === "high"
                                              ? "bg-red-50 text-red-700 border-red-200"
                                              : leadData.priority === "medium"
                                                ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                                : "bg-blue-50 text-blue-700 border-blue-200"
                                          )}
                                        >
                                          {leadData.priority === "high"
                                            ? "Hoch"
                                            : leadData.priority === "medium"
                                              ? "Mittel"
                                              : "Niedrig"}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="p-2">
                                        {(() => {
                                          const dateValue = getDateValue(leadData.nextContactDate);
                                          return dateValue
                                            ? (new Date(dateValue).getTime() ? new Date(dateValue).toLocaleDateString('de-DE') : "-")
                                            : "-";
                                        })()}
                                      </TableCell>
                                      <TableCell className="p-2">
                                        <div className="flex items-center">
                                          <Users className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                                          <span className="text-gray-600 text-sm">{assignedUserName}</span>
                                        </div>
                                      </TableCell>
                                      <TableCell className="p-2 flex justify-end">
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                              <MoreHorizontal className="h-4 w-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            <DropdownMenuLabel>Aktionen</DropdownMenuLabel>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                              onSelect={(e) => {
                                                e.preventDefault();
                                                setAssigningLead(contact);
                                              }}
                                            >
                                              <Users className="h-4 w-4 mr-2" />
                                              Zuweisen
                                            </DropdownMenuItem>
                                            {isAdmin && (
                                              <DropdownMenuItem
                                                onSelect={(e) => {
                                                  e.preventDefault();
                                                  handleTransferLead(contact);
                                                }}
                                              >
                                                <ArrowRight className="h-4 w-4 mr-2" />
                                                Übernehmen
                                              </DropdownMenuItem>
                                            )}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  {Object.keys(groupedOldContacts).length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">Keine Altkontakte verfügbar</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            // User view - show folder packages
            <div className="space-y-6">
              {/* Folder Packages */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(groupedNewLeads)
                  .filter(([folder]) => folder !== "Unsortiert") // Filter out "Unsortiert" folder
                  .map(([folder, leads]) => {
                    const availableLeads = leads.filter(l => l.status === "available");
                    if (availableLeads.length === 0) return null;

                    const avgPrice = availableLeads.reduce((sum, lead) => sum + lead.price, 0) / availableLeads.length;

                    return (
                      <Card key={folder} className="relative">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Folder className="h-5 w-5" />
                            {folder}
                          </CardTitle>
                          <CardDescription>
                            {availableLeads.length} Leads verfügbar
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor={`lead-count-${folder}`}>Anzahl der Leads</Label>
                              <div className="flex gap-2">
                                <Input
                                  id={`lead-count-${folder}`}
                                  type="number"
                                  min="1"
                                  max={availableLeads.length}
                                  value={customLeadCounts[folder] || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const numValue = parseInt(value);
                                    setCustomLeadCounts(prev => ({
                                      ...prev,
                                      [folder]: numValue > availableLeads.length ? availableLeads.length.toString() : value
                                    }));
                                  }}
                                  placeholder={`1-${availableLeads.length}`}
                                  className="flex-1"
                                />
                                <Button
                                  onClick={() => {
                                    if (!currentUser || !currentUser.id) {
                                      toast({
                                        title: "Nicht eingeloggt",
                                        description: "Sie müssen eingeloggt sein, um Leads zu kaufen.",
                                        variant: "destructive"
                                      });
                                      return;
                                    }

                                    const count = parseInt(customLeadCounts[folder] || "0");
                                    if (!count || count < 1 || count > availableLeads.length) {
                                      toast({
                                        title: "Ungültige Anzahl",
                                        description: `Bitte wählen Sie eine Anzahl zwischen 1 und ${availableLeads.length}.`,
                                        variant: "destructive"
                                      });
                                      return;
                                    }

                                    // Calculate total price
                                    const totalPrice = Math.round(avgPrice * count);

                                    // Create a package for the selected number of leads
                                    handlePurchasePackage(folder, count);
                                    // Reset only this folder's count
                                    setCustomLeadCounts(prev => ({
                                      ...prev,
                                      [folder]: ""
                                    }));
                                  }}
                                  disabled={!customLeadCounts[folder] || parseInt(customLeadCounts[folder]) < 1 || parseInt(customLeadCounts[folder]) > availableLeads.length}
                                >
                                  Kaufen
                                </Button>
                              </div>
                              {customLeadCounts[folder] && parseInt(customLeadCounts[folder]) > 0 && (
                                <p className="text-sm text-muted-foreground">
                                  Gesamtpreis: {Math.round(avgPrice * parseInt(customLeadCounts[folder])).toLocaleString('de-DE')} €
                                  ({avgPrice.toFixed(2)} € pro Lead)
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog for listing a lead for sale */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lead zum Verkauf anbieten</DialogTitle>
            <DialogDescription>
              Wählen Sie einen Lead aus und fügen Sie Details hinzu, um ihn auf dem Marktplatz anzubieten.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {ownLeads.length > 0 ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="lead-select">Lead auswählen</Label>
                  <Select onValueChange={(value) => {
                    const lead = ownLeads.find(l => l.id === value);
                    if (lead) setNewListingLead(lead);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Lead auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {ownLeads.map(lead => (
                        <SelectItem key={lead.id} value={lead.id}>
                          {lead.firstName} {lead.lastName} ({lead.company})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {newListingLead && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="price">Preis (€)</Label>
                        <Input
                          id="price"
                          type="number"
                          min="1"
                          value={newListingPrice}
                          onChange={(e) => setNewListingPrice(e.target.value)}
                          placeholder="100"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="folder">Ordner</Label>
                        <div className="space-y-2">
                          <Select
                            value={newListingFolder}
                            onValueChange={setNewListingFolder}
                          >
                            <SelectTrigger id="folder">
                              <SelectValue placeholder="Ordner auswählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {folders.map(folder => (
                                <SelectItem key={folder} value={folder}>
                                  {folder}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Neuen Ordner erstellen"
                              value={newFolderName}
                              onChange={(e) => setNewFolderName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddFolder(newFolderName);
                                  setNewListingFolder(newFolderName);
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                handleAddFolder(newFolderName);
                                setNewListingFolder(newFolderName);
                              }}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Beschreibung</Label>
                      <Textarea
                        id="description"
                        value={newListingDescription}
                        onChange={(e) => setNewListingDescription(e.target.value)}
                        placeholder="Beschreiben Sie den Lead und warum er wertvoll ist"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="type">Lead-Typ</Label>
                      <Select
                        value={newListingLead.status === "old-contact" ? "old-contact" : "new"}
                        onValueChange={(value) => {
                          // Update the lead type
                          setNewListingLead(prev => prev ? {
                            ...prev,
                            status: value as "new" | "old-contact"
                          } : null);
                        }}
                      >
                        <SelectTrigger id="type">
                          <SelectValue placeholder="Lead-Typ wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Neuer Lead</SelectItem>
                          <SelectItem value="old-contact">Altkontakt</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {newListingLead.status === "old-contact" && (
                      <div className="space-y-2">
                        <Label htmlFor="followUpDate">Wiedervorlage-Datum</Label>
                        <Input
                          id="followUpDate"
                          type="date"
                          value={newListingLead.nextContactDate ?
                            new Date(newListingLead.nextContactDate).toISOString().split('T')[0] :
                            ""
                          }
                          onChange={(e) => {
                            setNewListingLead(prev => prev ? {
                              ...prev,
                              nextContactDate: e.target.value
                            } : null);
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted-foreground">
                  Sie haben noch keine Leads, die Sie zum Verkauf anbieten können.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleListLeadForSale}
              disabled={!newListingLead || !newListingPrice || !newListingFolder || !newListingDescription || ownLeads.length === 0}
            >
              Zum Verkauf anbieten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog for creating a lead order */}
      <Dialog open={isOrderDialogOpen} onOpenChange={setIsOrderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Individuales angebot erstellen</DialogTitle>
            <DialogDescription>
              Geben Sie die gewünschte Anzahl von Leads für Ihr individuelles Angebot ein.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Anzahl der Leads</Label>
              <Input
                id="amount"
                type="number"
                min="1"
                value={newOrderAmount}
                onChange={(e) => setNewOrderAmount(e.target.value)}
                placeholder="z.B. 15"
              />
            </div>

            {newOrderAmount && parseInt(newOrderAmount) > 0 && (
              <div className="space-y-2">
                <Label>Geschätzter Durchschnittspreis</Label>
                <div className="text-sm text-muted-foreground">
                  {(() => {
                    const availableLeads = marketData.newLeads.filter(l =>
                      l.type === "new" && l.status === "available"
                    );
                    if (availableLeads.length === 0) return "Keine Leads verfügbar";

                    const avgPrice = availableLeads.reduce((sum, lead) => sum + lead.price, 0) /
                      availableLeads.length;
                    const total = avgPrice * parseInt(newOrderAmount);

                    return `${avgPrice.toFixed(2)} € pro Lead (Gesamt: ${total.toFixed(2)} €)`;
                  })()}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsOrderDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={() => handleCreateOrder(parseInt(newOrderAmount))}
              disabled={!newOrderAmount || parseInt(newOrderAmount) <= 0}
            >
              Angebot erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Leads Dialog */}
      <Dialog open={isAddLeadsDialogOpen} onOpenChange={setIsAddLeadsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Leads zur Börse hinzufügen</DialogTitle>
            <DialogDescription>
              Fügen Sie ausgewählte Leads aus Ihrer Lead-Liste zur Börse hinzu.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Ausgewählte Leads</Label>
              <div className="border rounded-md p-4 max-h-[200px] overflow-y-auto">
                {selectedLeads.length > 0 ? (
                  <ul className="space-y-2">
                    {selectedLeads.map(lead => (
                      <li key={lead.id} className="flex items-center justify-between">
                        <span>
                          {lead.firstName} {lead.lastName} ({lead.company})
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLeads(prev =>
                            prev.filter(l => l.id !== lead.id)
                          )}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Keine Leads ausgewählt. Bitte wählen Sie Leads aus Ihrer Lead-Liste aus.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bulk-price">Preis pro Lead (€)</Label>
                <Input
                  id="bulk-price"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                  placeholder="z.B. 1.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk-folder">Ordner</Label>
                <Select
                  value={bulkFolder}
                  onValueChange={setBulkFolder}
                >
                  <SelectTrigger id="bulk-folder">
                    <SelectValue placeholder="Ordner wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {folders.slice(1).map(folder => (
                      <SelectItem key={folder} value={folder}>
                        {folder}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk-description">Beschreibung</Label>
              <Textarea
                id="bulk-description"
                value={bulkDescription}
                onChange={(e) => setBulkDescription(e.target.value)}
                placeholder="Beschreibung für die ausgewählten Leads"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAddLeadsDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleAddLeadsToMarketplace}
              disabled={selectedLeads.length === 0 || !bulkPrice || !bulkFolder || !bulkDescription}
            >
              Zur Börse hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <CSVImport
        isOpen={isImportOpen}
        onClose={() => {
          setIsImportOpen(false);
          setImportPrice("");
          setImportFolder("");
          setImportDescription("");
        }}
        onImport={handleImportLeads}
        marketplaceFields={{
          price: importPrice,
          folderName: importFolder,
          description: importDescription,
          onPriceChange: setImportPrice,
          onFolderChange: (value) => {
            setImportFolder(value);
            if (!folders.includes(value)) {
              setFolders(prev => [...prev, value]);
            }
          },
          onDescriptionChange: setImportDescription,
          folders: folders
        }}
      />

      {/* Folder Management Dialog */}
      <Dialog open={isFolderManagementOpen} onOpenChange={setIsFolderManagementOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Ordner verwalten</DialogTitle>
            <DialogDescription>
              Verwalten Sie Ihre Ordner und organisieren Sie Ihre Leads.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {folders.map(folder => (
              <div key={folder} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <Folder className="h-5 w-5 text-muted-foreground" />
                  {editingFolder?.oldName === folder ? (
                    <Input
                      value={editingFolder.newName}
                      onChange={(e) => setEditingFolder(prev => ({ ...prev!, newName: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameFolder(folder, editingFolder.newName);
                          setEditingFolder(null);
                        } else if (e.key === 'Escape') {
                          setEditingFolder(null);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <span className="font-medium">{folder}</span>
                  )}
                  <Badge variant="secondary">
                    {marketData.newLeads.filter(lead => lead.folderName === folder).length} Leads
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {editingFolder?.oldName === folder ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          handleRenameFolder(folder, editingFolder.newName);
                          setEditingFolder(null);
                        }}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingFolder(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingFolder({ oldName: folder, newName: folder })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const folderLeads = marketData.newLeads.filter(lead => lead.folderName === folder);
                          setMovingLeads({ folder, leads: folderLeads });
                        }}
                      >
                        <Move className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFolderToDelete(folder)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setNewFolderName("");
                setFolders(prev => [...prev, "Neuer Ordner"]);
                setEditingFolder({ oldName: "Neuer Ordner", newName: "Neuer Ordner" });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Neuer Ordner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Leads Dialog */}
      <Dialog
        open={!!movingLead}
        onOpenChange={() => setMovingLead(null)}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Lead verschieben</DialogTitle>
            <DialogDescription>
              Wählen Sie den Zielordner für den Lead von {movingLead?.leadData.firstName} {movingLead?.leadData.lastName} ({movingLead?.leadData.company}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Zielordner</Label>
              <Select
                onValueChange={(value) => {
                  if (movingLead) {
                    handleMoveLead(movingLead, value);
                    setMovingLead(null);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Ordner auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {folders
                    .filter(f => f !== movingLead?.folderName)
                    .map(folder => (
                      <SelectItem key={folder} value={folder}>
                        {folder}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Confirmation Dialog */}
      <AlertDialog open={!!folderToDelete} onOpenChange={() => setFolderToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ordner löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Ordner "{folderToDelete}" wirklich löschen?
              Alle Leads in diesem Ordner werden in den Ordner "Unsortiert" verschoben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (folderToDelete) {
                  handleDeleteFolder(folderToDelete);
                  setFolderToDelete(null);
                }
              }}
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Lead Dialog */}
      <Dialog
        open={!!assigningLead}
        onOpenChange={(open) => {
          if (!open) {
            setAssigningLead(null);
            setSelectedUser("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Lead zuweisen</DialogTitle>
            <DialogDescription>
              Wählen Sie einen Berater aus, dem der Lead zugewiesen werden soll.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Berater auswählen</Label>
              {isLoadingUsers ? (
                <div className="text-sm text-muted-foreground">Lade Berater...</div>
              ) : usersError ? (
                <div className="text-sm text-destructive">
                  Fehler beim Laden der Berater. Bitte versuchen Sie es später erneut.
                </div>
              ) : users.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Keine Berater verfügbar.
                </div>
              ) : (
                <Select
                  value={selectedUser}
                  onValueChange={setSelectedUser}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Berater auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleUsers
                      .map(user => (
                        <SelectItem
                          key={user.id}
                          value={user.id}
                          className={!user.active ? "text-muted-foreground opacity-60" : ""}
                        >
                          {user.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Folder Selection */}
            <div className="grid gap-2">
              <Label htmlFor="folder-select">Ordner (optional)</Label>
              <Select
                value={selectedFolderId || "none"}
                onValueChange={(value) => setSelectedFolderId(value === "none" ? null : value)}
                disabled={!selectedUser}
              >
                <SelectTrigger id="folder-select">
                  <SelectValue placeholder="Kein Ordner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Ordner</SelectItem>
                  {userFolders.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAssigningLead(null);
                setSelectedUser("");
                setSelectedFolderId("");
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                if (assigningLead && selectedUser) {
                  handleAssignLead(assigningLead, selectedUser);
                }
              }}
              disabled={!selectedUser || isLoadingUsers || !!usersError || isAssigningLead}
            >
              {isAssigningLead ? "Zuweisen..." : "Zuweisen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog
        open={!!bulkAssigningFolder}
        onOpenChange={(open) => {
          if (!open) {
            setBulkAssigningFolder(null);
            setSelectedUser("");
            setSelectedFolderId("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Leads zuweisen</DialogTitle>
            <DialogDescription>
              Wählen Sie einen Berater aus, dem alle {bulkAssigningFolder?.leads.length} Leads aus dem Ordner "{bulkAssigningFolder?.folder}" zugewiesen werden sollen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Berater auswählen</Label>
              {isLoadingUsers ? (
                <div className="text-sm text-muted-foreground">Lade Berater...</div>
              ) : usersError ? (
                <div className="text-sm text-destructive">
                  Fehler beim Laden der Berater. Bitte versuchen Sie es später erneut.
                </div>
              ) : users.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Keine Berater verfügbar.
                </div>
              ) : (
                <Select
                  value={selectedUser}
                  onValueChange={setSelectedUser}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Berater auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleUsers
                      .map(user => (
                        <SelectItem
                          key={user.id}
                          value={user.id}
                          className={!user.active ? "text-muted-foreground opacity-60" : ""}
                        >
                          {user.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Folder Selection */}
            <div className="grid gap-2">
              <Label htmlFor="bulk-folder-select">Ordner (optional)</Label>
              <Select
                value={selectedFolderId || "none"}
                onValueChange={(value) => setSelectedFolderId(value === "none" ? null : value)}
                disabled={!selectedUser}
              >
                <SelectTrigger id="bulk-folder-select">
                  <SelectValue placeholder="Kein Ordner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Ordner</SelectItem>
                  {userFolders.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBulkAssigningFolder(null);
                setSelectedUser("");
                setSelectedFolderId("");
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                if (bulkAssigningFolder && selectedUser) {
                  handleBulkAssignLeads(bulkAssigningFolder.folder, bulkAssigningFolder.leads, selectedUser);
                  setSelectedFolderId("");
                }
              }}
              disabled={!selectedUser || isLoadingUsers || !!usersError || isBulkAssigning}
            >
              {isBulkAssigning ? "Zuweisen..." : `${bulkAssigningFolder?.leads.length} Leads zuweisen`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Lead Confirmation Dialog */}
      <AlertDialog
        open={!!deletingLead}
        onOpenChange={() => setDeletingLead(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lead löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Lead von {deletingLead?.leadData.firstName} {deletingLead?.leadData.lastName} wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingLead) {
                  handleDeleteLead(deletingLead);
                  setDeletingLead(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Folder Leads Confirmation Dialog */}
      <AlertDialog
        open={!!deletingFolder}
        onOpenChange={() => setDeletingFolder(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alle Leads löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie wirklich alle {deletingFolder?.leads.length} Leads aus dem Ordner "{deletingFolder?.folder}" löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingFolder) {
                  handleBulkDeleteLeads(deletingFolder.folder, deletingFolder.leads);
                  setDeletingFolder(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Alle löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Transfer Dialog */}
      <Dialog
        open={!!bulkTransferringFolder}
        onOpenChange={(open) => {
          if (!open) {
            setBulkTransferringFolder(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Leads übernehmen</DialogTitle>
            <DialogDescription>
              Möchten Sie wirklich alle {bulkTransferringFolder?.leads.length} Leads aus dem Ordner "{bulkTransferringFolder?.folder}" übernehmen?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBulkTransferringFolder(null);
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                if (bulkTransferringFolder) {
                  handleBulkTransferLeads(bulkTransferringFolder.folder, bulkTransferringFolder.leads);
                }
              }}
            >
              {bulkTransferringFolder?.leads.length} Leads übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Count-based Assignment Dialog */}
      <Dialog open={isCountAssignDialogOpen} onOpenChange={setIsCountAssignDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Leads zuweisen</DialogTitle>
            <DialogDescription>
              Wählen Sie einen Berater aus, dem {assignCount} Leads aus dem Ordner "{countAssigningFolder?.folder}" zugewiesen werden sollen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Anzahl der Leads</Label>
              <Input
                type="number"
                min="1"
                max={countAssigningFolder?.leads.length || 1}
                value={assignCount}
                onChange={(e) => setAssignCount(e.target.value)}
                placeholder="z.B. 5"
              />
              <p className="text-sm text-muted-foreground">
                Verfügbare Leads: {countAssigningFolder?.leads.length || 0}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Berater auswählen</Label>
              {isLoadingUsers ? (
                <div className="text-sm text-muted-foreground">Lade Berater...</div>
              ) : usersError ? (
                <div className="text-sm text-destructive">
                  Fehler beim Laden der Berater. Bitte versuchen Sie es später erneut.
                </div>
              ) : users.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Keine Berater verfügbar.
                </div>
              ) : (
                <Select
                  value={selectedUser}
                  onValueChange={setSelectedUser}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Berater auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleUsers
                      .map(user => (
                        <SelectItem
                          key={user.id}
                          value={user.id}
                          className={!user.active ? "text-muted-foreground opacity-60" : ""}
                        >
                          {user.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Folder Selection */}
            <div className="grid gap-2">
              <Label htmlFor="count-folder-select">Ordner (optional)</Label>
              <Select
                value={selectedFolderId || "none"}
                onValueChange={(value) => setSelectedFolderId(value === "none" ? null : value)}
                disabled={!selectedUser}
              >
                <SelectTrigger id="count-folder-select">
                  <SelectValue placeholder="Kein Ordner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Ordner</SelectItem>
                  {userFolders.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCountAssignDialogOpen(false);
                setSelectedFolderId("");
              }}
              disabled={isAssigningCount}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                if (countAssigningFolder && selectedUser && assignCount) {
                  handleCountAssignLeads(countAssigningFolder.folder, countAssigningFolder.leads, selectedUser, parseInt(assignCount));
                  setSelectedFolderId("");
                }
              }}
              disabled={!selectedUser || isLoadingUsers || !!usersError || !assignCount || isAssigningCount}
            >
              {isAssigningCount ? "Zuweisen..." : `${assignCount} Leads zuweisen`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
